/**
 * Subscription Monetization Interface - Subscription Banner
 * 
 * Dashboard Banner features:
 * - If downgrade scheduled: "Your plan will downgrade on [date]"
 * - Trial ending warning
 * - Quota approaching limit warnings
 * 
 * Goal: Drive urgency for upgrades, prevent surprise billing changes.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { 
  AlertTriangle, ArrowDown, Clock, X, CreditCard,
  ArrowUpCircle, CalendarDays, Zap
} from 'lucide-react';

const SubscriptionBanner = ({
  type = 'downgrade', // 'downgrade' | 'trial_ending' | 'quota_warning' | 'payment_failed'
  subscription,
  pendingDowngrade,
  trialEndsAt,
  quotaType,
  quotaCurrent,
  quotaMax,
  onUpgrade,
  onCancelDowngrade,
  onDismiss,
}) => {
  const { t } = useTranslation();
  
  // Downgrade scheduled banner
  if (type === 'downgrade' && pendingDowngrade) {
    const downgradeTo = pendingDowngrade.to_package_name || pendingDowngrade.toPackageName;
    
    return (
      <div 
        className="bg-amber-50 border-b border-amber-200 px-4 py-3"
        data-testid="downgrade-scheduled-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
              <ArrowDown className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-amber-800">
                {t('banner.downgradingTo', 'Your plan will downgrade to {{plan}} at the end of your billing period', {
                  plan: downgradeTo,
                })}
              </p>
              <p className="text-xs text-amber-600">
                {t('banner.currentPlanActive', 'Your current plan remains active until then')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onCancelDowngrade && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={onCancelDowngrade}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                data-testid="cancel-downgrade-banner-btn"
              >
                {t('banner.cancelDowngrade', 'Keep Current Plan')}
              </Button>
            )}
            {onDismiss && (
              <button 
                onClick={onDismiss}
                className="p-1 text-amber-600 hover:bg-amber-100 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Trial ending banner
  if (type === 'trial_ending' && trialEndsAt) {
    const endDate = new Date(trialEndsAt);
    const daysRemaining = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
    const isUrgent = daysRemaining <= 2;
    
    return (
      <div 
        className={`${isUrgent ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'} border-b px-4 py-3`}
        data-testid="trial-ending-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 ${isUrgent ? 'bg-red-100' : 'bg-blue-100'} rounded-full flex items-center justify-center`}>
              <Clock className={`h-4 w-4 ${isUrgent ? 'text-red-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <p className={`text-sm font-medium ${isUrgent ? 'text-red-800' : 'text-blue-800'}`}>
                {daysRemaining > 0 
                  ? t('banner.trialEndsIn', 'Your trial ends in {{days}} days', { days: daysRemaining })
                  : t('banner.trialEndsToday', 'Your trial ends today!')
                }
              </p>
              <p className={`text-xs ${isUrgent ? 'text-red-600' : 'text-blue-600'}`}>
                {t('banner.upgradeToKeepAccess', 'Upgrade now to keep access to all features')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onUpgrade && (
              <Button 
                size="sm" 
                className={isUrgent ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
                onClick={onUpgrade}
                data-testid="upgrade-trial-banner-btn"
              >
                <ArrowUpCircle className="h-4 w-4 mr-2" />
                {t('banner.upgradePlan', 'Upgrade Now')}
              </Button>
            )}
            {onDismiss && (
              <button 
                onClick={onDismiss}
                className={`p-1 ${isUrgent ? 'text-red-600 hover:bg-red-100' : 'text-blue-600 hover:bg-blue-100'} rounded`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Quota warning banner
  if (type === 'quota_warning' && quotaType && quotaMax > 0) {
    const percentage = Math.round((quotaCurrent / quotaMax) * 100);
    const isAtLimit = percentage >= 100;
    const isNearLimit = percentage >= 80;
    
    if (!isNearLimit) return null;
    
    const quotaLabels = {
      leads: t('banner.leadsQuota', 'leads'),
      salesReps: t('banner.salesRepsQuota', 'included users'),
      offers: t('banner.offersQuota', 'offers'),
    };
    
    return (
      <div 
        className={`${isAtLimit ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border-b px-4 py-3`}
        data-testid="quota-warning-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 ${isAtLimit ? 'bg-red-100' : 'bg-amber-100'} rounded-full flex items-center justify-center`}>
              <AlertTriangle className={`h-4 w-4 ${isAtLimit ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <p className={`text-sm font-medium ${isAtLimit ? 'text-red-800' : 'text-amber-800'}`}>
                {isAtLimit 
                  ? t('banner.quotaReached', 'You\'ve reached your {{quota}} limit ({{current}}/{{max}})', {
                      quota: quotaLabels[quotaType],
                      current: quotaCurrent,
                      max: quotaMax,
                    })
                  : t('banner.quotaNearLimit', 'You\'re approaching your {{quota}} limit ({{current}}/{{max}})', {
                      quota: quotaLabels[quotaType],
                      current: quotaCurrent,
                      max: quotaMax,
                    })
                }
              </p>
              <p className={`text-xs ${isAtLimit ? 'text-red-600' : 'text-amber-600'}`}>
                {t('banner.upgradeForMore', 'Upgrade for higher limits')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onUpgrade && (
              <Button 
                size="sm" 
                className={isAtLimit ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
                onClick={onUpgrade}
                data-testid="upgrade-quota-banner-btn"
              >
                <Zap className="h-4 w-4 mr-2" />
                {t('banner.increaseLimit', 'Increase Limit')}
              </Button>
            )}
            {onDismiss && !isAtLimit && (
              <button 
                onClick={onDismiss}
                className={`p-1 ${isAtLimit ? 'text-red-600 hover:bg-red-100' : 'text-amber-600 hover:bg-amber-100'} rounded`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Payment failed banner
  if (type === 'payment_failed') {
    return (
      <div 
        className="bg-red-50 border-b border-red-200 px-4 py-3"
        data-testid="payment-failed-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <CreditCard className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-red-800">
                {t('banner.paymentFailed', 'Payment failed for your subscription')}
              </p>
              <p className="text-xs text-red-600">
                {t('banner.updatePayment', 'Please update your payment method to avoid service interruption')}
              </p>
            </div>
          </div>
          <Button 
            size="sm" 
            className="bg-red-600 hover:bg-red-700"
            data-testid="update-payment-banner-btn"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {t('banner.updatePaymentMethod', 'Update Payment')}
          </Button>
        </div>
      </div>
    );
  }
  
  return null;
};

export default SubscriptionBanner;
